import './styles.scss';
import React, { useState, useRef, useEffect } from "react";
import Modal from "@amzn/awsui-components-console/modal";
import Box from "@amzn/awsui-components-console/box";
import SpaceBetween from "@amzn/awsui-components-console/space-between";
import Button from "@amzn/awsui-components-console/button";
import Flashbar from "@amzn/awsui-components-console/flashbar";
import Checkbox from "@amzn/awsui-components-console/checkbox";
import { PROCESS_KEYS } from "../Constants";
import { AgGridReact } from "ag-grid-react";
import { FileUpload } from "@amzn/awsui-components-react";
import * as XLSX from 'xlsx';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import { delsertClean } from "src/Ateam-BI/Templates/Utils/AppActivityUtilsAppsyncPlus";
import { createAuditLog, getCurrentTime } from "src/Ateam-BI/Templates/Utils/AppActivityUtilsAppsyncPlus"


const errorBannerConstants = {
  header: "Error submitting data",
  type: "error",
  dismissable: true,
  dismissLabel: "Dismiss message",
  onDismiss: () => setNotification([])
}

const generateColumnDefs = (data, isDirty) => data.map((column, index) => {
  const colDef = {
    field: column[PROCESS_KEYS.SCHEMA_KEYS.column_name],
    editable: true,
    checkboxSelection: index === 0,
    required: column.mandatory,
    cellClass: params => {
      if (isDirty && column.mandatory && (!params.value || params.value.toString().trim() === "")) {
        return 'error-cell';
      }
      return '';
    }
  };
  return colDef;
});

const addNewRow = (setRowData, colDefs) => {
  const newRow = {};
  colDefs.forEach(col => {
    newRow[col.field] = "";
  });
  setRowData(prevRowData => [newRow, ...prevRowData]);
};

const refreshCellStyles = (gridRef, data, isValid, setColDefs) => {
  setColDefs(generateColumnDefs(data, !isValid));
  gridRef.current.api.refreshCells({ force: true });
};

const deleteRows = (rowsToDelete, setRowData, gridRef, colDefs, setIsDirty, isDirty, setColDefs, data) => {
  const indicesToDelete = new Set(rowsToDelete.map(row => row.rowIndex));
  setRowData(prevRowData => prevRowData.filter((_, index) => !indicesToDelete.has(index)));
  if (isDirty) {
    setTimeout(() => {
      const allRows = [];
      gridRef.current.api.forEachNode(node => allRows.push(node.data));
      const isValid = validateRowData(allRows, colDefs, setIsDirty);
      if (isValid) {
        refreshCellStyles(gridRef, data, isValid, setColDefs);
      }
    }, 0);
  }
}

const validateRowData = (rowData, colDefs, setIsDirty, setNotification) => {
  const requiredFields = colDefs.filter(col => col.required).map(col => col.field);
  const hasEmptyRequired = rowData.some(row => 
    requiredFields.some(field => !row[field] || row[field].toString().trim() === "")
  );
  if (hasEmptyRequired) {
    setNotification([
      {
        ...errorBannerConstants,
        content: "Please fill out all required fields",
        id: "error-notification-1"
      }
    ])
    setIsDirty(true);
    return false;
  }
  setIsDirty(false);
  return true;
};

const submitData = (setVisible, gridRef, colDefs, setIsDirty, setRowData, setColDefs, setNotification, deleteBlankValues, data, id, pid, userId) => {
  const payload = {
    delete_blank_values: deleteBlankValues,
    data: []
  }

  try {
    gridRef.current.api.forEachNode(node => payload.data.push(node.data));
    setRowData(payload.data);
    if (!validateRowData(payload.data, colDefs, setIsDirty, setNotification)) {
      refreshCellStyles(gridRef, data, false, setColDefs);
      return;
    }

    createAuditLog(`FRISCO`, `DELSERT_CLEAN#${getCurrentTime()}`, "Cleaning delsert keys", JSON.stringify({}), JSON.stringify(payload), "DELSERT_CLEAN", userId)
    delsertClean(
      id,
      pid,
      payload.data,
      deleteBlankValues,
      userId,
      () => {
        setVisible(false);
      },
      (error) => {
        console.error("Error:", error);
        console.error("Error details:", JSON.stringify(error, null, 2));
        const errorMsg = error?.errors?.[0]?.message || "Failed to cleanup data. Please contact your administrator.";
        setNotification([{
          ...errorBannerConstants,
          content: errorMsg,
          id: "error-notification-2"
        }]);
      }
    )

  } catch (e) {
    console.error(e);
  }
}

const CleanupDataModal = ({ visible, setVisible, data, id, pid, userId }) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [colDefs, setColDefs] = useState(generateColumnDefs(data, isDirty));
  const [multiSelectedRow, setMultiSelectedRow] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [notification, setNotification] = useState([]);
  const [deleteBlankValues, setDeleteBlankValues] = useState(false);

  const onSelectionChanged = (param) => {
    const selectedNodes = gridRef.current.api.getSelectedNodes();
    const selectedData = selectedNodes.map(node => ({
      rowIndex: Number.parseInt(node.id),
      data: node.data
    }));
    setMultiSelectedRow(selectedData.sort((a, b) => b.rowIndex - a.rowIndex));
  };

  const onCellValueChanged = (event) => {
    const allRows = [];
    gridRef.current.api.forEachNode(node => allRows.push(node.data));
    setRowData(allRows);
    if (isDirty) {
      const isValid = validateRowData(allRows, colDefs, setIsDirty);
      if (isValid) {
        refreshCellStyles(gridRef, data, isValid, setColDefs);
      }
    }
  };

  const fileContent = async (uploadedFiles, setRowData) => {
    const allData = [];
    const validFields = colDefs.map(col => col.field);
    for (const file of uploadedFiles) {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      const filteredData = jsonData.map(row => {
        const filteredRow = {};
        validFields.forEach(field => {
          if (row.hasOwnProperty(field)) {
            filteredRow[field] = row[field];
          }
        });
        return filteredRow;
      });
      allData.push(...filteredData);
    }
    setRowData(allData);
  };

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      fileContent(uploadedFiles, setRowData);
      setIsDirty(false);
    }
  }, [uploadedFiles])

  return (
    <Modal
      onDismiss={() => setVisible(false)}
      visible={visible}
      size="max"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">

            <Button data-testid="cancelBtn" variant="link" onClick={() => setVisible(false)}>Cancel</Button>
            <Button data-testid="submitBtn" variant="primary" onClick={() => submitData(setVisible, gridRef, colDefs, setIsDirty, setRowData, setColDefs, setNotification, deleteBlankValues, data, id, pid, userId)} disabled={isDirty || !(rowData.length > 0)}>Submit</Button>
          </SpaceBetween>
        </Box>
      }
      header="Cleanup Data"
    >
      <div className="cleanup-data-modal--content">
        <div className="notifications-wrapper">
          <Flashbar items={notification} />
        </div>
        <div className="ag-theme-alpine">
          <div className="table-header">
            <div className="table-actions-wrapper">
              <Button data-testid="addBtn" onClick={(() => addNewRow(setRowData, colDefs))} iconName="insert-row">Add</Button>
              <Button data-testid="deleteBtn" onClick={(() => deleteRows(multiSelectedRow, setRowData, gridRef, colDefs, setIsDirty, isDirty, setColDefs, data))} disabled={multiSelectedRow?.length <= 0} iconName="delete-marker">Delete</Button>
            </div>
            <div className="file-upload-wrapper">
              <FileUpload
                accept=".csv"
                onChange={({ detail }) => setUploadedFiles(detail.value)}
                value={uploadedFiles}
                i18nStrings={{
                  uploadButtonText: e =>
                    e ? "Choose files" : "Choose file",
                  dropzoneText: e =>
                    e
                      ? "Drop files to upload"
                      : "Drop file to upload",
                  removeFileAriaLabel: e =>
                    `Remove file ${e + 1}`,
                  limitShowFewer: "Show fewer files",
                  limitShowMore: "Show more files",
                  errorIconAriaLabel: "Error",
                  warningIconAriaLabel: "Warning"
                }}
                showFileLastModified
                showFileSize
                showFileThumbnail
                tokenLimit={3}
                constraintText="Hint text for file requirements"
              />
            </div>
            <div className="table-options-wrapper">
              <Checkbox
                onChange={({ detail }) =>
                  setDeleteBlankValues(detail.checked)
                }
                checked={deleteBlankValues}
              >
                Delete Blank Values
              </Checkbox>
            </div>
          </div>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={colDefs}
            singleClickEdit={true}
            stopEditingWhenCellsLoseFocus={true}
            rowSelection='multiple'
            onSelectionChanged={onSelectionChanged}
            suppressRowClickSelection={true}
            rowMultiSelectWithClick={true}
            animateRows={true}
            rowHeight={30}
            undoRedoCellEditing={true}
            undoRedoCellEditingLimit={10}
            onCellValueChanged={onCellValueChanged}
            onCellFocused={() => { }}
            onGridReady={() => { }}
            onColumnRowGroupChanged={() => { }}
            pivotMode={false}
            suppressScrollOnNewData={true}
          />
        </div>
      </div>
    </Modal>
  );
}

export default CleanupDataModal;